import React, { memo, useCallback, useMemo } from "react";
import { TableBody, TableFooter, TableHeader } from "./components";
import { ColumnProperty, TableProps } from "./types";
import _ from "lodash";
import { Sort } from "services/search";
import { Loader } from "ui/components/Loader";
import NoResults from "../../NoResults/NoResults";
import { classNames } from "helpers";
import { ColumnToggle, useColumnVisibility } from "ui/components/Table/ColumnToggle";

const TableSearch: React.FC<TableProps> = (props) => {
  const {
    data,
    columns,
    selectableRows = true,
    selectedRows,
    disablePagination = false,
    pagination,
    disableSelection,
    showSelectAll,
    onPaginationChange,
    onSelectedChange,
    onRowClick,
    onEventClick,
    isLoading,
    isMinimized,
    columnsChild,
    CustomTableRowChild,
    childTableTitle,
    rowClassName,
    size = "auto",
  } = props;

  const hasColumnKeys = useMemo(
    () => columns.some((c) => !!c.key),
    [columns]
  );

  const storageKey = useMemo(
    () =>
      columns
        .filter((c) => c.key)
        .map((c) => c.key)
        .join("."),
    [columns]
  );

  const { visibleColumns, columnStates, toggleColumn } = useColumnVisibility(
    columns,
    storageKey
  );

  const activeColumns = hasColumnKeys ? visibleColumns : columns;

  const columnProperties: ColumnProperty[] = useMemo(
    () =>
      _.map(activeColumns, (c) => ({
        title: c.title,
        sortable: !!c.sortable,
        field: c.field || "",
        tooltip: c.tooltip,
      })),
    [activeColumns]
  );

  const handleSelectAllClick = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!onSelectedChange) {
        return;
      }

      const current = selectedRows ?? [];
      const pageIds = data.map((d) => d.id);

      if (!event.target.checked) {
        // Odznači samo redove trenutne stranice; selekcija s drugih stranica ostaje.
        onSelectedChange(current.filter((id) => !pageIds.includes(id)));
        return;
      }

      // Označi redove trenutne stranice + zadrži postojeću selekciju (puni skup → consumer radi replace).
      onSelectedChange([...new Set([...current, ...pageIds])]);
    },
    [data, selectedRows, onSelectedChange]
  );

  const handleSelectCheckboxClick = useCallback(
    (id: number) => {
      if (onSelectedChange) {
        onSelectedChange(id);
      }
    },
    [onSelectedChange]
  );

  const handleSort = useCallback(
    (sort: Sort) => {
      if (onPaginationChange && pagination) {
        onPaginationChange({ ...pagination, sort });
      }
    },
    [pagination, onPaginationChange]
  );

  if (!isLoading && pagination?.totalPages === 0) {
    return <NoResults />;
  }

  return (

    <div className="flex flex-col relative ">
      {isLoading && data.length === 0 ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader isLoading={true}>{null}</Loader>
        </div>
      ) : (
        <div
          className={classNames(
            // Stupci se ne stisnu ispod čitljivog, pa se tablica na užem ekranu pomiče vodoravno.
            // Izbornik retka to ne pogađa — otvara se u portalu, izvan ovog omotača.
            isMinimized
              ? "overflow-x-scroll scrollbar-hide xl:scrollbar-default "
              : "overflow-x-auto ",
            "inline-block min-w-full align-middle w-[calc(100%-80px)] "
          )}
        >
          <div
            className={classNames(
              size === "auto" && "h-auto ",
              size === "small" && "h-[25vh] ",
              size === "medium" && "h-[50vh] ",
              size === "large" && "h-[75vh] ",
              size === "full" && "h-[100vh]"
            )}
          >
            <table
              cellPadding={0}
              cellSpacing={0}
              className="min-w-full whitespace-nowrap divide-y divide-[#ebebeb] border-collapse p-0"
            >
              <TableHeader
                columnProperties={columnProperties}
                selectableItems={selectableRows}
                rowCount={data.length}
                numSelected={
                  data.filter((d) => (selectedRows ?? []).includes(d.id)).length
                }
                onSelectAllClick={handleSelectAllClick}
                sort={pagination ? pagination.sort : undefined}
                onSort={handleSort}
                disableSelection={disableSelection}
                showSelectAll={showSelectAll}
                columnToggle={
                  hasColumnKeys ? (
                    <ColumnToggle
                      columnStates={columnStates}
                      onToggle={toggleColumn}
                    />
                  ) : undefined
                }
              />
              <TableBody
                data={data}
                columns={activeColumns}
                selectableRows={selectableRows}
                selectedRows={selectedRows || []}
                onRowClick={onRowClick}
                onEventClick={onEventClick}
                onSelectCheckboxClick={handleSelectCheckboxClick}
                disableSelection={disableSelection}
                columnsChild={columnsChild}
                CustomTableRowChild={CustomTableRowChild}
                childTableTitle={childTableTitle}
                rowClassName={rowClassName}
              />
              {!disablePagination && (
                <TableFooter
                  columnCount={columnProperties.length + 1}
                  pagination={pagination}
                  onPaginationChange={onPaginationChange}
                  isMinimized={isMinimized}
                />
              )}
            </table>
          </div>
          {isLoading && <Loader isLoading={true}>{null}</Loader>}
        </div>
      )}
    </div>
  );
};

export default memo(TableSearch);
